import React, { useEffect, useState, useRef } from "react";
import LoadingSpinner from "../../components/LoadingSpinner";
import { useIndexPageData } from "../../contextapi/Indexpagedata";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { getCategoryIcon } from "../../utils/categoryIcons";

const CategoryIcon = ({ name }) => {
  const Icon = getCategoryIcon(name);

  return (
    <span className="category-nav-bar__icon category-nav-bar__icon--fallback" aria-hidden="true">
      <Icon size={22} />
    </span>
  );
};

const CategoryDropdowns = ({
  selectedCategoryId,
  selectedSubCategoryId,
  selectedSubSubCategoryId,
  onCategoryChange,
  onSubCategoryChange,
  onSubSubCategoryChange,
  onFetch,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navRef = useRef(null);
  const { nestedSubCategories, categories } = useIndexPageData();
  const categoriesLoader = !categories?.length;
  const [hoveredCategoryId, setHoveredCategoryId] = useState(null);
  const [hoveredSubId, setHoveredSubId] = useState(null);
  const [displaySubId, setDisplaySubId] = useState(null);

  const mergedCategories = React.useMemo(() => {
    if (!categories?.length || !nestedSubCategories?.length) return categories || [];
    const grouped = (nestedSubCategories || []).reduce((acc, subsub) => {
      if (subsub.has_listings) {
        const subId = subsub.subcategory;
        if (!acc[subId]) acc[subId] = [];
        acc[subId].push(subsub);
      }
      return acc;
    }, {});

    return (categories || [])
      .filter((cat) => cat.has_listings)
      .map((category) => ({
        ...category,
        subcategories: (category.subcategories || [])
          .filter((sub) => sub.has_listings)
          .map((sub) => ({
            ...sub,
            subsubcategories: grouped[sub.id] || [],
          })),
      }));
  }, [categories, nestedSubCategories]);

  const hoveredCategory = mergedCategories.find(
    (c) => String(c.id) === String(hoveredCategoryId),
  );
  const subcategories = hoveredCategory?.subcategories || [];
  const activeSubId = displaySubId ?? hoveredSubId;
  const subForSubSubs = activeSubId
    ? subcategories.find((s) => String(s.id) === String(activeSubId))
    : null;
  const subsubcategories = subForSubSubs?.subsubcategories || [];
  const showSubSubColumn = activeSubId != null && subsubcategories.length > 0;

  const handleCategoryClick = (catId) => {
    onCategoryChange(catId);
    onSubCategoryChange(null);
    onSubSubCategoryChange(null);
    setHoveredCategoryId(null);
    setHoveredSubId(null);
    setDisplaySubId(null);
    onFetch({ categoryId: catId, subCategoryId: null, subSubCategoryId: null });
  };

  const handleSubCategoryClick = (catId, subId) => {
    onCategoryChange(catId);
    onSubCategoryChange(subId);
    onSubSubCategoryChange(null);
    setHoveredCategoryId(null);
    setHoveredSubId(null);
    setDisplaySubId(null);
    onFetch({ categoryId: catId, subCategoryId: subId, subSubCategoryId: null });
  };

  const handleSubSubCategoryClick = (catId, subId, subsubId) => {
    onCategoryChange(catId);
    onSubCategoryChange(subId);
    onSubSubCategoryChange(subsubId);
    setHoveredCategoryId(null);
    setHoveredSubId(null);
    setDisplaySubId(null);
    onFetch({
      categoryId: catId,
      subCategoryId: subId,
      subSubCategoryId: subsubId,
    });
  };

  const closeDropdown = () => {
    setHoveredCategoryId(null);
    setHoveredSubId(null);
    setDisplaySubId(null);
  };

  const handleCategoryButtonClick = (cat) => {
    const hasSubs = cat.subcategories?.length > 0;

    if (isMobile && hasSubs) {
      if (String(hoveredCategoryId) === String(cat.id)) {
        handleCategoryClick(cat.id);
      } else {
        setHoveredCategoryId(cat.id);
        setHoveredSubId(null);
        setDisplaySubId(null);
      }
      return;
    }

    handleCategoryClick(cat.id);
  };

  const handleSubItemClick = (catId, sub) => {
    const hasSubSubs = sub.subsubcategories?.length > 0;

    if (isMobile && hasSubSubs) {
      if (String(displaySubId) === String(sub.id)) {
        handleSubCategoryClick(catId, sub.id);
      } else {
        setHoveredSubId(sub.id);
        setDisplaySubId(sub.id);
      }
      return;
    }

    handleSubCategoryClick(catId, sub.id);
  };

  useEffect(() => {
    if (!isMobile || hoveredCategoryId == null) return;

    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isMobile, hoveredCategoryId]);

  const isCategoryActive = (catId) => String(selectedCategoryId) === String(catId);
  const isSubActive = (subId) => String(selectedSubCategoryId) === String(subId);
  const isSubSubActive = (subsubId) =>
    String(selectedSubSubCategoryId) === String(subsubId);

  const isPanelOpen = hoveredCategoryId != null;

  if (categoriesLoader) {
    return (
      <div className="d-flex align-items-center gap-2 mb-4">
        <LoadingSpinner color="#C93312" />
        <span className="small text-muted">Loading categories…</span>
      </div>
    );
  }

  return (
    <div
      ref={navRef}
      className={`category-nav-bar mb-4${isPanelOpen ? " category-nav-bar--panel-open" : ""}`}
      onMouseLeave={isMobile ? undefined : closeDropdown}
    >
      <nav
        className="category-nav-bar__list"
        role="navigation"
        aria-label="Category menu"
      >
        {mergedCategories.map((cat) => {
          const isOpen = String(hoveredCategoryId) === String(cat.id);
          const isActive = isCategoryActive(cat.id);
          const hasSubs = cat.subcategories?.length > 0;

          return (
            <div
              key={cat.id}
              className={`category-nav-bar__item${isOpen ? " category-nav-bar__item--open" : ""}`}
              onMouseEnter={
                isMobile
                  ? undefined
                  : () => {
                      setHoveredCategoryId(cat.id);
                      setDisplaySubId(null);
                      setHoveredSubId(null);
                    }
              }
            >
              <button
                type="button"
                onClick={() => handleCategoryButtonClick(cat)}
                className={`category-nav-bar__trigger${
                  isActive || isOpen ? " category-nav-bar__trigger--active" : ""
                }`}
                aria-expanded={hasSubs ? isOpen : undefined}
                aria-haspopup={hasSubs ? "true" : undefined}
              >
                <CategoryIcon name={cat.name} />
                <span className="category-nav-bar__label">
                  <span className="category-nav-bar__label-text">{cat.name}</span>
                  {hasSubs && (
                    <KeyboardArrowDownIcon
                      className="category-nav-bar__chevron"
                      sx={{ fontSize: "1rem" }}
                    />
                  )}
                </span>
              </button>
            </div>
          );
        })}
      </nav>

      {isPanelOpen && hoveredCategory && (
        <div
          className="category-nav-bar__mega-panel"
          onMouseEnter={isMobile ? undefined : () => setHoveredCategoryId(hoveredCategory.id)}
        >
          <div
            className={`category-nav-bar__mega-columns${isMobile ? " category-nav-bar__mega-columns--stacked" : ""}${!showSubSubColumn ? " category-nav-bar__mega-columns--subs-only" : ""}`}
          >
            <div className="category-nav-bar__mega-col category-nav-bar__mega-col--subs">
              <p className="category-nav-bar__mega-heading">{hoveredCategory.name}</p>
              {subcategories.length === 0 ? (
                <p className="category-nav-bar__empty mb-0">No subcategories</p>
              ) : (
                <ul className="category-nav-bar__sub-list">
                  {subcategories.map((sub) => {
                    const isSubHighlighted =
                      String(hoveredSubId) === String(sub.id) ||
                      String(displaySubId) === String(sub.id);

                    return (
                      <li key={sub.id}>
                        <button
                          type="button"
                          className={`category-nav-bar__sub-btn${
                            isSubActive(sub.id) ? " category-nav-bar__sub-btn--active" : ""
                          }${isSubHighlighted ? " category-nav-bar__sub-btn--hover" : ""}`}
                          onMouseEnter={
                            isMobile
                              ? undefined
                              : () => {
                                  setHoveredSubId(sub.id);
                                  setDisplaySubId(sub.id);
                                }
                          }
                          onMouseLeave={isMobile ? undefined : () => setHoveredSubId(null)}
                          onClick={() => handleSubItemClick(hoveredCategory.id, sub)}
                        >
                          <span>{sub.name}</span>
                          {sub.subsubcategories?.length > 0 && (
                            <ChevronRightIcon sx={{ fontSize: "1.1rem", opacity: 0.45 }} />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {showSubSubColumn && (
              <div className="category-nav-bar__mega-col category-nav-bar__mega-col--subsubs">
                <p className="category-nav-bar__mega-heading">
                  {subForSubSubs?.name}
                </p>
                <ul className="category-nav-bar__subsub-list category-nav-bar__subsub-list--panel">
                  {subsubcategories.map((subsub) => (
                    <li key={subsub.id}>
                      <button
                        type="button"
                        className={`category-nav-bar__subsub-btn${
                          isSubSubActive(subsub.id)
                            ? " category-nav-bar__subsub-btn--active"
                            : ""
                        }`}
                        onClick={() =>
                          handleSubSubCategoryClick(
                            hoveredCategory.id,
                            activeSubId,
                            subsub.id,
                          )
                        }
                      >
                        {subsub.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <button
            type="button"
            className="category-nav-bar__view-all"
            onClick={() => handleCategoryClick(hoveredCategory.id)}
          >
            View all {hoveredCategory.name}
            <ChevronRightIcon sx={{ fontSize: "1rem" }} />
          </button>
        </div>
      )}
    </div>
  );
};

export default CategoryDropdowns;
